import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import {
	collateralizationRatio,
	getRiskZoneFromCr,
	LIQUIDATION_THRESHOLD,
	maxLtvFromCr,
	MIN_COLLATERAL_RATIO,
	riskZoneColor,
} from "@/lib/defi-math";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

/**
 * Circular vault gauge.
 * Fill = mint-capacity utilization (LTV / maxMintLtv), not raw CR 1.0→3.0
 * so full borrow at 1.50× fills the ring (~100%), not ~25%.
 */
export function RatioGauge({
	collateralUsd,
	debtUsdm,
	ltvPct,
	healthFactor: _healthFactor,
	/** Live protocol liquidationThreshold as CR */
	liquidationCr = LIQUIDATION_THRESHOLD,
	/** Live protocol min collateral ratio (mint floor) */
	minCr = MIN_COLLATERAL_RATIO,
	className,
	size = 180,
}: {
	collateralUsd: number;
	debtUsdm: number;
	ltvPct?: number;
	/** Kept for call-site compatibility; CR is derived from coll/debt */
	healthFactor?: number;
	liquidationCr?: number;
	minCr?: number;
	className?: string;
	size?: number;
}) {
	void _healthFactor;
	const mintCr = minCr > 0 ? minCr : MIN_COLLATERAL_RATIO;
	const liqCr = liquidationCr > 0 ? liquidationCr : LIQUIDATION_THRESHOLD;
	const maxMintLtv = maxLtvFromCr(mintCr);

	const crMult = collateralizationRatio(collateralUsd, debtUsdm);
	const ltv =
		ltvPct ??
		(collateralUsd > 0 ? (debtUsdm / collateralUsd) * 100 : 0);
	const zone = getRiskZoneFromCr(crMult, mintCr, liqCr);
	const color = riskZoneColor(zone);
	const stroke = 12;
	const r = (size - stroke) / 2;
	const c = 2 * Math.PI * r;

	// Utilization of mint capacity: 0% empty vault → 100% at mint floor
	const util =
		maxMintLtv > 0 && ltv > 0
			? Math.min(1, Math.max(0, ltv / maxMintLtv))
			: 0;
	const offset = c * (1 - util);

	const label = useMemo(() => {
		switch (zone) {
			case "safe":
				return "Healthy";
			case "moderate":
				return "Caution";
			case "danger":
				return "At risk";
			case "liquidatable":
				return "Liquidatable";
			case "underwater":
				return "Underwater";
		}
	}, [zone]);

	return (
		<div
			className={cn("flex flex-col items-center gap-2", className)}
			role="img"
			aria-label={`Collateralization ${Number.isFinite(crMult) ? crMult.toFixed(2) : "∞"}×, LTV ${ltv.toFixed(1)}% (${(util * 100).toFixed(0)}% of mint capacity), ${label}`}
		>
			<span
				className="rounded-full px-3 py-1 text-xs font-semibold"
				style={{
					color,
					backgroundColor: `${color}18`,
					border: `1px solid ${color}55`,
				}}
			>
				{label}
			</span>

			<svg
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
				className="overflow-visible"
				aria-hidden
			>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={r}
					fill="none"
					stroke="var(--bg-muted)"
					strokeWidth={stroke}
				/>
				<circle
					cx={size / 2}
					cy={size / 2}
					r={r}
					fill="none"
					stroke={color}
					strokeWidth={stroke}
					strokeLinecap="round"
					strokeDasharray={c}
					strokeDashoffset={offset}
					transform={`rotate(-90 ${size / 2} ${size / 2})`}
					style={{
						transition:
							"stroke-dashoffset 600ms ease, stroke 400ms ease",
					}}
				/>
				<text
					x="50%"
					y="44%"
					textAnchor="middle"
					dominantBaseline="middle"
					className="fill-[var(--text-primary)] font-mono text-[1.65rem] font-bold"
					style={{ fontFamily: "var(--font-mono)" }}
				>
					{Number.isFinite(crMult) ? `${crMult.toFixed(2)}×` : "∞"}
				</text>
				<text
					x="50%"
					y="58%"
					textAnchor="middle"
					className="fill-[var(--text-muted)] text-[10px] font-semibold uppercase tracking-wider"
				>
					Coll. ratio
				</text>
			</svg>

			<div className="grid w-full max-w-[220px] grid-cols-2 gap-2 text-center text-xs">
				<div className="rounded-xl bg-muted/80 px-2 py-2">
					<p className="text-muted-foreground">LTV</p>
					<p className="font-mono-amount text-sm font-bold">
						<AnimatedNumber value={ltv} decimals={1} suffix="%" />
					</p>
				</div>
				<div className="rounded-xl bg-muted/80 px-2 py-2">
					<p className="text-muted-foreground">Mint used</p>
					<p className="font-mono-amount text-sm font-bold">
						<AnimatedNumber value={util * 100} decimals={0} suffix="%" />
					</p>
				</div>
			</div>
			<p className="text-[10px] text-muted-foreground">
				Mint floor {mintCr.toFixed(2)}× · Liq. CR {liqCr.toFixed(2)}× · Max LTV{" "}
				{maxMintLtv.toFixed(1)}%
			</p>
		</div>
	);
}
